import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTarget, FiTrendingUp, FiShield, FiEye} = FiIcons;

const StrategistPath = () => {
  const advantages = [
    {
      icon: FiTrendingUp,
      title: '"ข้อมูลเชิงลึกมิติที่สาม"',
      description: 'เปลี่ยนสัญชาตญาณที่คลุมเครือ ให้กลายเป็นข้อมูลด้าน "เวลา" ที่เฉียบคม ใช้อธิบายทิศทางให้ทีมและนักลงทุนเชื่อมั่นได้อย่างไร้ข้อกังขา'
    },
    {
      icon: FiShield,
      title: '"ศาสตร์แห่งการบริหารความเสี่ยง"',
      description: 'การเข้าใจผัง Bazi จะช่วยให้คุณหยั่งรู้ "ฤดูกาล" ของธุรกิจและชีวิต ทำให้คุณรู้ว่าเมื่อใดควรรุกฆาต เมื่อใดควรรักษาค่าย และเมื่อใดควรซุ่มรอโอกาส'
    },
    {
      icon: FiEye,
      title: '"ความได้เปรียบที่มองไม่เห็น"',
      description: 'ในขณะที่คู่แข่งของคุณมองเห็นแค่ตัวเลขบนดิน คุณจะมองเห็นกระแสพลังงานจากผัง ปาจื้อ นี่คือความแตกต่างระหว่าง "ผู้เล่น" และ "ผู้บัญชาการเกม"'
    }
  ];

  return (
    <section className="py-20 relative" id="strategist">
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-indigo-900/20 to-slate-900"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
        >
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center mr-4">
              <SafeIcon icon={FiTarget} className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-4xl md:text-5xl font-light text-white">สร้างความได้เปรียบด้วย Bazi</h2>
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-2xl p-8 mb-12 border border-purple-500/20 backdrop-blur-sm">
            <p className="text-2xl text-purple-100 font-light italic">
              "มีอะไรอีกบ้าง ที่จะทำให้ฉันไปได้ไกลและเร็วกว่านี้?"
            </p>
          </div>
        </motion.div>

        <motion.div
          className="bg-white/5 backdrop-blur-lg rounded-3xl p-10 border border-white/10 mb-12"
          initial={{opacity: 0, scale: 0.9}}
          whileInView={{opacity: 1, scale: 1}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
        >
          <p className="text-lg text-slate-300 leading-relaxed font-light mb-8">
            คุณสร้างความสำเร็จด้วยสมองและสองมือ คุณเชื่อในข้อมูล ตรรกะ และความสามารถของตนเอง แต่ในสมรภูมิที่ดุเดือด... สิ่งเหล่านี้อาจยังไม่เพียงพอ คุณสัมผัสได้ว่า <span className="text-purple-400 font-medium">"จังหวะ"</span> คือตัวแปรสำคัญที่แยกผู้ชนะออกจากผู้พ่ายแพ้
          </p>
          <p className="text-lg text-slate-300 leading-relaxed font-light">
            ศาสตร์แห่ง <span className="text-indigo-400 font-medium">ปาจื้อ (八字,Bazi)</span> คือ <span className="text-purple-400 font-medium">"เครื่องมือวิเคราะห์จังหวะเวลาเชิงกลยุทธ์"</span> ที่จะมอบ:
          </p>
        </motion.div>

        <div className="space-y-8 mb-12">
          {advantages.map((advantage, index) => (
            <motion.div
              key={index}
              className="bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/10"
              initial={{opacity: 0, x: index % 2 === 0 ? -50 : 50}}
              whileInView={{opacity: 1, x: 0}}
              transition={{duration: 0.8, delay: index * 0.2}}
              viewport={{once: true}}
            >
              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={advantage.icon} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl text-white font-light mb-3">{advantage.title}</h4>
                  <p className="text-slate-300 leading-relaxed font-light">{advantage.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonial */}
        <motion.div
          className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-3xl p-10 border border-purple-500/20 backdrop-blur-sm text-center"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.5}}
          viewport={{once: true}}
        >
          <div className="mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-light text-2xl">N</span>
            </div>
          </div>
          <blockquote className="text-xl text-purple-100 italic font-light leading-relaxed mb-6">
            "ผมเคยคิดว่าข้อมูลคือทุกสิ่ง แต่คอร์สนี้สอนให้ผมเข้าใจ 'เวลา' ผ่านหลักการของ Bazi มันคือมิติที่ขาดหายไปในการทำธุรกิจ ตอนนี้ทุกการตัดสินใจของผมมีทั้ง 'ศาสตร์' และ 'ศิลป์' ประกอบกัน"
          </blockquote>
          <cite className="text-purple-300 font-light">
            — นริน,CEO & Co-founder
          </cite>
        </motion.div>
      </div>
    </section>
  );
};

export default StrategistPath;